import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../config/axios';
import Navbar from '../components/Navbar';
import * as adminApi from '../api/adminApi';
import { makeTenantUrl } from '../utils/tenantUrl';
import './Dashboard.css';

const SUBDOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const RESERVED_SUBDOMAINS = ['www', 'api', 'admin', 'mail', 'ftp', 'test'];
const ADMIN_TOKEN_KEY = 'adminToken';
const ADMIN_EMAIL_KEY = 'adminEmail';
const ADMIN_ROLE_KEY = 'adminRole';

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {}
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch (_) {
    return false;
  }
}

const PowerUserDashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    admins: 0,
    managers: 0,
    sites: 0
  });
  // Tenant Management (Super Admin)
  const [tenantSectionOpen, setTenantSectionOpen] = useState(false);
  const [adminToken, setAdminTokenState] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY));
  const [adminEmail, setAdminEmail] = useState(() => localStorage.getItem(ADMIN_EMAIL_KEY) || '');
  const [adminRole, setAdminRole] = useState(() => localStorage.getItem(ADMIN_ROLE_KEY) || '');
  const [adminLoginEmail, setAdminLoginEmail] = useState('');
  const [adminLoginPassword, setAdminLoginPassword] = useState('');
  const [adminLoginLoading, setAdminLoginLoading] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [tenants, setTenants] = useState([]);
  const [tenantSearchQ, setTenantSearchQ] = useState('');
  const [tenantListLoading, setTenantListLoading] = useState(false);
  const [createSubdomain, setCreateSubdomain] = useState('');
  const [createName, setCreateName] = useState('');
  const [createCompanyName, setCreateCompanyName] = useState('');
  const [createCountryCode, setCreateCountryCode] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createMessage, setCreateMessage] = useState('');
  const [seedTenantId, setSeedTenantId] = useState(null);
  const [seedEmail, setSeedEmail] = useState('');
  const [seedPassword, setSeedPassword] = useState('');
  const [seedName, setSeedName] = useState('');
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedMessage, setSeedMessage] = useState('');
  const [statusUpdating, setStatusUpdating] = useState({});
  const [lastCreatedTenant, setLastCreatedTenant] = useState(null);
  const [lastSeedInfo, setLastSeedInfo] = useState(null);
  const [showDeletedTenants, setShowDeletedTenants] = useState(false);
  const [deleteConfirmTenant, setDeleteConfirmTenant] = useState(null);
  const [deleteTenantLoading, setDeleteTenantLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState({});
  const [purgeConfirmTenant, setPurgeConfirmTenant] = useState(null);
  const [purgeTenantLoading, setPurgeTenantLoading] = useState(false);
  const [clearConfirmTenant, setClearConfirmTenant] = useState(null);
  const [clearTenantLoading, setClearTenantLoading] = useState(false);
  const [statsTenantId, setStatsTenantId] = useState('');
  const [activeTenantsForStats, setActiveTenantsForStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState({});
  const [restoreTenantTarget, setRestoreTenantTarget] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreDryRunResult, setRestoreDryRunResult] = useState(null);
  const [restoreLoading2, setRestoreLoading2] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState('');

  const setAdminToken = (token, email, role) => {
    if (token) {
      localStorage.setItem(ADMIN_TOKEN_KEY, token);
      if (email != null) localStorage.setItem(ADMIN_EMAIL_KEY, email);
      if (role != null) localStorage.setItem(ADMIN_ROLE_KEY, role);
      setAdminTokenState(token);
      setAdminEmail(email || '');
      setAdminRole(role || '');
    } else {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(ADMIN_EMAIL_KEY);
      localStorage.removeItem(ADMIN_ROLE_KEY);
      setAdminTokenState(null);
      setAdminEmail('');
      setAdminRole('');
    }
  };

  const loadTenants = async () => {
    if (!adminToken) return;
    setTenantListLoading(true);
    setAdminError('');
    try {
      const list = await adminApi.listTenants({ q: tenantSearchQ, includeDeleted: showDeletedTenants }, adminToken);
      setTenants(Array.isArray(list) ? list : []);
    } catch (e) {
      setAdminError(e.status ? `(${e.status}) ${e.message}` : e.message);
      setTenants([]);
    } finally {
      setTenantListLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tenantSectionOpen && adminToken) loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSectionOpen, adminToken, showDeletedTenants]);

  useEffect(() => {
    if (!adminToken) return;
    adminApi.listTenants({ includeDeleted: false }, adminToken).then(list => setActiveTenantsForStats(Array.isArray(list) ? list : [])).catch(() => setActiveTenantsForStats([]));
  }, [adminToken]);

  useEffect(() => {
    if (!adminToken) {
      setStats({ totalUsers: 0, admins: 0, managers: 0, sites: 0 });
      return;
    }
    setStatsLoading(true);
    if (statsTenantId) {
      adminApi.getTenantStats(statsTenantId, adminToken).then(s => setStats(s)).catch(() => setStats({ totalUsers: 0, admins: 0, managers: 0, sites: 0 })).finally(() => setStatsLoading(false));
    } else {
      adminApi.getAllTenantsStats(adminToken).then(s => setStats(s)).catch(() => setStats({ totalUsers: 0, admins: 0, managers: 0, sites: 0 })).finally(() => setStatsLoading(false));
    }
  }, [statsTenantId, adminToken]);

  const fetchData = async () => {
    try {
      const [usersRes, sitesRes] = await Promise.all([
        axiosInstance.get('/api/tenant/users'),
        axiosInstance.get('/api/tenant/sites')
      ]);
      const usersList = usersRes.data.users || [];
      const sitesList = sitesRes.data.sites || [];
      // Filter out Power Users - they should not appear in the list
      const filteredUsers = usersList.filter(u => u.role !== 'power_user');
      setUsers(filteredUsers);
      calculateStats(filteredUsers, sitesList.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      setUsers([]);
      setStats({ totalUsers: 0, admins: 0, managers: 0, sites: 0 });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (userList, sitesCount = 0) => {
    // Power Users are excluded from the list, so we don't count them
    const admins = userList.filter(u => u.role === 'admin');
    const managers = userList.filter(u => u.role === 'manager');
    
    setStats(prev => ({
      ...prev,
      totalUsers: userList.length,
      admins: admins.length,
      managers: managers.length,
      sites: sitesCount
    }));
  };


  const handleClearTenantDataConfirm = async () => {
    if (!clearConfirmTenant || !adminToken) return;
    setClearTenantLoading(true);
    setAdminError('');
    try {
      await adminApi.clearTenantData(clearConfirmTenant.id, adminToken);
      setClearConfirmTenant(null);
      loadTenants();
    } catch (e) {
      setAdminError(e.status ? `(${e.status}) ${e.message}` : e.message);
    } finally {
      setClearTenantLoading(false);
    }
  };


  const handleBackupTenant = async (tenantId, subdomain) => {
    setBackupLoading(prev => ({ ...prev, [tenantId]: true }));
    try {
      const res = await adminApi.backupTenant(tenantId, adminToken);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const cd = res.headers.get('content-disposition');
      let filename = 'Backup_' + (subdomain || 'tenant') + '_' + new Date().toISOString().split('T')[0] + '.zip';
      if (cd) { const m = cd.match(/filename="?(.+?)"?($|;)/); if (m) filename = m[1]; }
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setAdminError(e.message || 'Backup failed');
    } finally {
      setBackupLoading(prev => ({ ...prev, [tenantId]: false }));
    }
  };

  const handleRestoreSelect = (tenant) => {
    setRestoreTenantTarget(tenant);
    setRestoreFile(null);
    setRestoreDryRunResult(null);
    setRestoreMessage('');
  };

  const handleRestoreFileChosen = async (file) => {
    setRestoreFile(file);
    setRestoreDryRunResult(null);
    setRestoreMessage('');
    setRestoreLoading2(true);
    try {
      const result = await adminApi.restoreTenantDryRun(restoreTenantTarget.id, file, adminToken);
      setRestoreDryRunResult(result);
    } catch (e) {
      setRestoreMessage(e.message || 'Failed to validate backup file');
    } finally {
      setRestoreLoading2(false);
    }
  };

  const handleRestoreConfirm = async () => {
    if (!restoreTenantTarget || !restoreFile) return;
    setRestoreLoading2(true);
    setRestoreMessage('');
    try {
      const result = await adminApi.restoreTenantConfirm(restoreTenantTarget.id, restoreFile, adminToken);
      setRestoreMessage(result.message || 'Restore completed successfully');
      setTimeout(() => { setRestoreTenantTarget(null); loadTenants(); }, 2000);
    } catch (e) {
      setRestoreMessage(e.message || 'Restore failed');
    } finally {
      setRestoreLoading2(false);
    }
  };

  const handleAdminLogin = async () => {
    setAdminLoginLoading(true);
    setAdminError('');
    try {
      const res = await adminApi.adminLogin({ email: adminLoginEmail, password: adminLoginPassword });
      setAdminToken(res.token, res.admin?.email, res.admin?.role);
      setAdminLoginEmail('');
      setAdminLoginPassword('');
    } catch (e) {
      setAdminError(e.status ? `(${e.status}) ${e.message}` : e.message);
    } finally {
      setAdminLoginLoading(false);
    }
  };

  const handleAdminLogout = () => {
    setAdminToken(null);
    setTenants([]);
    setAdminError('');
  };

  const handleCreateTenant = async () => {
    const sub = (createSubdomain || '').trim().toLowerCase();
    const name = (createName || '').trim();
    if (!sub || !name) {
      setCreateMessage('Subdomain and name are required.');
      return;
    }
    if (!SUBDOMAIN_REGEX.test(sub)) {
      setCreateMessage('Subdomain must be lowercase letters, numbers, hyphens only (single label).');
      return;
    }
    if (RESERVED_SUBDOMAINS.includes(sub)) {
      setCreateMessage('This subdomain is reserved.');
      return;
    }
    setCreateLoading(true);
    setCreateMessage('');
    setAdminError('');
    try {
      const payload = { subdomain: sub, name };
      if ((createCompanyName || '').trim()) payload.companyName = (createCompanyName || '').trim();
      if ((createCountryCode || '').trim()) payload.countryCode = (createCountryCode || '').trim();
      const res = await adminApi.createTenant(payload, adminToken);
      setLastCreatedTenant({ subdomain: sub, name, id: res?.id });
      setCreateSubdomain('');
      setCreateName('');
      setCreateCompanyName('');
      setCreateCountryCode('');
      setCreateMessage('Tenant created successfully.');
      loadTenants();
      setTimeout(() => setCreateMessage(''), 4000);
    } catch (e) {
      setCreateMessage(e.status ? `(${e.status}) ${e.message}` : e.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSeedAdmin = async () => {
    if (!seedTenantId || !seedEmail.trim() || !seedPassword || !seedName.trim()) {
      setSeedMessage('Email, password, and name are required.');
      return;
    }
    setSeedLoading(true);
    setSeedMessage('');
    setAdminError('');
    try {
      await adminApi.seedTenantAdmin(seedTenantId, { email: seedEmail.trim(), password: seedPassword, name: seedName.trim() }, adminToken);
      const t = tenants.find(x => x.id === seedTenantId);
      setLastSeedInfo({ subdomain: t?.subdomain ?? '', tenantName: t?.name ?? '', email: seedEmail.trim() });
      setSeedTenantId(null);
      setSeedEmail('');
      setSeedPassword('');
      setSeedName('');
      setSeedMessage('Tenant admin created successfully.');
      loadTenants();
      setTimeout(() => setSeedMessage(''), 4000);
    } catch (e) {
      setSeedMessage(e.status ? `(${e.status}) ${e.message}` : e.message);
    } finally {
      setSeedLoading(false);
    }
  };

  const handleUpdateTenantStatus = async (tenantId, status) => {
    setStatusUpdating(prev => ({ ...prev, [tenantId]: true }));
    setAdminError('');
    try {
      await adminApi.updateTenantStatus(tenantId, { status }, adminToken);
      loadTenants();
    } catch (e) {
      setAdminError(e.status ? `(${e.status}) ${e.message}` : e.message);
    } finally {
      setStatusUpdating(prev => ({ ...prev, [tenantId]: false }));
    }
  };

  const handleTenantStatusChange = (tenant, newStatus) => {
    if (newStatus === 'deleted') {
      setDeleteConfirmTenant({ id: tenant.id, subdomain: tenant.subdomain, name: tenant.name });
      return;
    }
    handleUpdateTenantStatus(tenant.id, newStatus);
  };

  const handleConfirmDeleteTenant = async () => {
    if (!deleteConfirmTenant) return;
    setDeleteTenantLoading(true);
    setAdminError('');
    try {
      await adminApi.deleteTenant(deleteConfirmTenant.id, adminToken);
      setDeleteConfirmTenant(null);
      loadTenants();
    } catch (e) {
      setAdminError(e.status ? `(${e.status}) ${e.message}` : e.message);
    } finally {
      setDeleteTenantLoading(false);
    }
  };

  const handleRestoreTenant = async (tenantId) => {
    setRestoreLoading(prev => ({ ...prev, [tenantId]: true }));
    setAdminError('');
    try {
      await adminApi.restoreTenant(tenantId, adminToken);
      loadTenants();
    } catch (e) {
      setAdminError(e.status ? `(${e.status}) ${e.message}` : e.message);
    } finally {
      setRestoreLoading(prev => ({ ...prev, [tenantId]: false }));
    }
  };

  const handleConfirmPurgeTenant = async () => {
    if (!purgeConfirmTenant) return;
    setPurgeTenantLoading(true);
    setAdminError('');
    try {
      await adminApi.purgeTenant(purgeConfirmTenant.id, adminToken);
      setPurgeConfirmTenant(null);
      loadTenants();
    } catch (e) {
      setAdminError(e.status ? `(${e.status}) ${e.message}` : e.message);
    } finally {
      setPurgeTenantLoading(false);
    }
  };

  if (!adminToken) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ textAlign: 'center', padding: '40px' }}>
          <h2>Power User Dashboard</h2>
          <p style={{ margin: '20px 0' }}>You must log in as admin to access this page.</p>
          <Link to="/admin-login" className="btn btn-primary">Go to Admin Login</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="app-layout">
      <Navbar user={{ name: adminEmail, email: adminEmail, role: adminRole || 'super_admin' }} onLogout={handleAdminLogout} />
      <div className="dashboard">
        <div className="dashboard-header">
          <h2>Power User Dashboard</h2>
          <p>Full access to manage all users and system settings</p>
        </div>

        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label htmlFor="stats-tenant-select">Stats for:</label>
          <select
            id="stats-tenant-select"
            value={statsTenantId}
            onChange={e => setStatsTenantId(e.target.value)}
            style={{ padding: '6px 10px', minWidth: '200px' }}
          >
            <option value="">All tenants</option>
            {activeTenantsForStats.map(t => (
              <option key={t.id} value={t.id}>{t.name} ({t.subdomain})</option>
            ))}
          </select>
          {statsLoading && <span style={{ fontSize: '13px', color: '#666' }}>Loading…</span>}
        </div>

        <div className="dashboard-stats">
          <div className="stat-card">
            <h3>Total Users</h3>
            <div className="stat-value">{stats.totalUsers}</div>
          </div>
          <div className="stat-card">
            <h3>Admins</h3>
            <div className="stat-value">{stats.admins}</div>
          </div>
          <div className="stat-card">
            <h3>Managers</h3>
            <div className="stat-value">{stats.managers}</div>
          </div>
          <div className="stat-card">
            <h3>Sites</h3>
            <div className="stat-value">{stats.sites}</div>
          </div>
        </div>

        {adminToken && (
          <div style={{ marginTop: '24px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => setTenantSectionOpen(!tenantSectionOpen)}
              style={{
                width: '100%',
                padding: '14px 20px',
                textAlign: 'left',
                background: '#f8f9fa',
                border: 'none',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Tenant Management (Super Admin) {tenantSectionOpen ? '▼' : '▶'}
            </button>
            {tenantSectionOpen && (
              <div style={{ padding: '20px', background: '#fff' }}>
                <div style={{ padding: '10px 14px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', marginBottom: '16px' }}>
                  Admin endpoints are IP-restricted by Nginx. This will only work from an allowlisted IP.
                </div>

                {adminError && (
                  <div style={{ marginBottom: '12px', padding: '10px', background: '#f8d7da', color: '#721c24', borderRadius: '6px' }}>{adminError}</div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px' }}>Admin Login</h4>
                  {adminToken ? (
                    <div>
                      <p style={{ margin: '0 0 8px' }}>Logged in as <strong>{adminEmail}</strong> ({adminRole})</p>
                      <button type="button" className="btn btn-secondary" onClick={handleAdminLogout}>Logout admin</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="email"
                        placeholder="Admin Email"
                        value={adminLoginEmail}
                        onChange={e => setAdminLoginEmail(e.target.value)}
                        style={{ padding: '8px 12px', width: '200px' }}
                      />
                      <input
                        type="password"
                        placeholder="Admin Password"
                        value={adminLoginPassword}
                        onChange={e => setAdminLoginPassword(e.target.value)}
                        style={{ padding: '8px 12px', width: '180px' }}
                      />
                      <button type="button" className="btn btn-primary" onClick={handleAdminLogin} disabled={adminLoginLoading}>
                        {adminLoginLoading ? 'Logging in...' : 'Login as Super Admin'}
                      </button>
                    </div>
                  )}
                </div>

                {adminToken && (
                  <>
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ margin: '0 0 10px' }}>Create Tenant</h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                        <input
                          placeholder="Subdomain (e.g. hanauk)"
                          value={createSubdomain}
                          onChange={e => setCreateSubdomain(e.target.value)}
                          style={{ padding: '8px 12px', width: '140px' }}
                        />
                        <input
                          placeholder="Name"
                          value={createName}
                          onChange={e => setCreateName(e.target.value)}
                          style={{ padding: '8px 12px', width: '180px' }}
                        />
                        <input
                          placeholder="Company name (optional, e.g. hanagroup)"
                          value={createCompanyName}
                          onChange={e => setCreateCompanyName(e.target.value)}
                          style={{ padding: '8px 12px', width: '200px' }}
                          title="Used on root URL: entering this name shows a country picker when multiple tenants share it"
                        />
                        <input
                          placeholder="Country (optional, e.g. UK, IE)"
                          value={createCountryCode}
                          onChange={e => setCreateCountryCode(e.target.value)}
                          style={{ padding: '8px 12px', width: '120px' }}
                          title="Shown in the country picker when users enter the company name at the root URL"
                        />
                        <button type="button" className="btn btn-primary" onClick={handleCreateTenant} disabled={createLoading}>
                          {createLoading ? 'Creating...' : 'Create Tenant'}
                        </button>
                      </div>
                      {createMessage && <p style={{ margin: '8px 0 0', color: createMessage.startsWith('(') ? '#721c24' : '#155724' }}>{createMessage}</p>}
                      {lastCreatedTenant && (
                        <div style={{ marginTop: '12px', padding: '12px', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px' }}>
                          <p style={{ margin: '0 0 6px', fontWeight: 600 }}>Tenant created: {lastCreatedTenant.name} ({lastCreatedTenant.subdomain})</p>
                          <p style={{ margin: '0 0 8px' }}>Tenant URL: <a href={makeTenantUrl(lastCreatedTenant.subdomain)} target="_blank" rel="noopener noreferrer">{makeTenantUrl(lastCreatedTenant.subdomain)}</a></p>
                          {lastCreatedTenant.id && <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#6c757d' }}>Tenant ID: {lastCreatedTenant.id}</p>}
                          <button type="button" className="btn btn-sm btn-secondary" onClick={async () => { const ok = await copyToClipboard(makeTenantUrl(lastCreatedTenant.subdomain)); setCreateMessage(ok ? 'URL copied' : 'Copy failed'); setTimeout(() => setCreateMessage(''), 2000); }}>Copy URL</button>
                        </div>
                      )}
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ margin: '0 0 10px' }}>Tenants</h4>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                          placeholder="Search (subdomain/name)"
                          value={tenantSearchQ}
                          onChange={e => setTenantSearchQ(e.target.value)}
                          style={{ padding: '8px 12px', width: '220px' }}
                        />
                        <button type="button" className="btn btn-secondary" onClick={loadTenants} disabled={tenantListLoading}>
                          {tenantListLoading ? 'Loading...' : 'Search'}
                        </button>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={showDeletedTenants}
                            onChange={e => setShowDeletedTenants(e.target.checked)}
                          />
                          Show deleted
                        </label>
                      </div>
                      {tenantListLoading && tenants.length === 0 ? (
                        <p>Loading tenants...</p>
                      ) : tenants.length === 0 ? (
                        <p style={{ color: '#666' }}>No tenants. Log in and click Search, or create one above.</p>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                              <th style={{ textAlign: 'left', padding: '8px' }}>Subdomain</th>
                              <th style={{ textAlign: 'left', padding: '8px' }}>Name</th>
                              <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
                              <th style={{ textAlign: 'left', padding: '8px' }}>Created</th>
                              <th style={{ textAlign: 'left', padding: '8px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tenants.map(t => (
                              <tr key={t.id} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '8px' }}>
                                  <div>{t.subdomain}</div>
                                  {t.id && <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>Tenant ID: {t.id}</div>}
                                </td>
                                <td style={{ padding: '8px' }}>{t.name}</td>
                                <td style={{ padding: '8px' }}>
                                  <span style={{ backgroundColor: t.status === 'active' ? '#d4edda' : t.status === 'suspended' ? '#fff3cd' : '#f8d7da', color: t.status === 'active' ? '#155724' : t.status === 'suspended' ? '#856404' : '#721c24', padding: '2px 8px', borderRadius: '4px', marginRight: '8px', fontSize: '12px' }}>{t.status}</span>
                                  <select
                                    value={t.status}
                                    onChange={e => handleTenantStatusChange(t, e.target.value)}
                                    disabled={statusUpdating[t.id]}
                                    style={{ padding: '4px 8px' }}
                                  >
                                    <option value="active">active</option>
                                    <option value="suspended">suspended</option>
                                    <option value="deleted">deleted</option>
                                  </select>
                                  {statusUpdating[t.id] && <span style={{ marginLeft: '6px' }}>Updating...</span>}
                                </td>
                                <td style={{ padding: '8px' }}>{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '—'}</td>
                                <td style={{ padding: '8px' }}>
                                  {t.status !== 'deleted' && (
                                    <>
                                      <a href={makeTenantUrl(t.subdomain)} target="_blank" rel="noopener noreferrer" style={{ marginRight: '6px', fontSize: '13px' }}>Open</a>
                                      <button type="button" className="btn btn-sm btn-secondary" style={{ marginRight: '6px' }} onClick={async () => { await copyToClipboard(makeTenantUrl(t.subdomain)); }} title="Copy tenant URL">Copy URL</button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => setSeedTenantId(seedTenantId === t.id ? null : t.id)}
                                      >
                                        {seedTenantId === t.id ? 'Cancel' : 'Seed Admin'}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        style={{ marginLeft: '6px' }}
                                        onClick={() => setClearConfirmTenant({ id: t.id, subdomain: t.subdomain, name: t.name })}
                                        disabled={clearTenantLoading}
                                      >
                                        Clear All Data
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        style={{ marginLeft: '6px' }}
                                        onClick={() => handleBackupTenant(t.id, t.subdomain)}
                                        disabled={backupLoading[t.id]}
                                      >
                                        {backupLoading[t.id] ? 'Backing up...' : 'Backup'}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-secondary"
                                        style={{ marginLeft: '6px' }}
                                        onClick={() => handleRestoreSelect({ id: t.id, subdomain: t.subdomain, name: t.name })}
                                      >
                                        Restore
                                      </button>
                                    </>
                                  )}
                                  {t.status === 'deleted' && (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleRestoreTenant(t.id)}
                                        disabled={restoreLoading[t.id]}
                                        style={{ marginRight: '6px' }}
                                      >
                                        {restoreLoading[t.id] ? 'Restoring...' : 'Restore'}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => setPurgeConfirmTenant({ id: t.id, subdomain: t.subdomain, name: t.name })}
                                      >
                                        Permanently delete
                                      </button>
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {deleteConfirmTenant && (
                      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => !deleteTenantLoading && setDeleteConfirmTenant(null)}>
                        <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', maxWidth: '420px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                          <h4 style={{ margin: '0 0 12px' }}>Permanently delete tenant data?</h4>
                          <p style={{ margin: '0 0 16px', color: '#333' }}>
                            This will permanently delete all data for <strong>{deleteConfirmTenant.name}</strong> ({deleteConfirmTenant.subdomain}): users, sites, inventory, counts. The tenant will be marked as deleted and can be restored later as an empty tenant.
                          </p>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn-secondary" onClick={() => setDeleteConfirmTenant(null)} disabled={deleteTenantLoading}>Cancel</button>
                            <button type="button" className="btn btn-danger" onClick={handleConfirmDeleteTenant} disabled={deleteTenantLoading}>
                              {deleteTenantLoading ? 'Deleting...' : 'Confirm permanent delete'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {clearConfirmTenant && (
                      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => !clearTenantLoading && setClearConfirmTenant(null)}>
                        <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', maxWidth: '420px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                          <h4 style={{ margin: '0 0 12px' }}>Clear all data for this tenant?</h4>
                          <p style={{ margin: '0 0 16px', color: '#333' }}>This will remove all users, sites, inventory, counts, and drafts for <strong>{clearConfirmTenant.name}</strong> ({clearConfirmTenant.subdomain}). The tenant will stay active so you can use Seed Admin immediately. This cannot be undone.</p>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn-secondary" onClick={() => setClearConfirmTenant(null)} disabled={clearTenantLoading}>Cancel</button>
                            <button type="button" className="btn btn-danger" onClick={handleClearTenantDataConfirm} disabled={clearTenantLoading}>{clearTenantLoading ? 'Clearing...' : 'Clear All Data'}</button>
                          </div>
                        </div>
                      </div>
                    )}

                    {purgeConfirmTenant && (
                      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => !purgeTenantLoading && setPurgeConfirmTenant(null)}>
                        <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', maxWidth: '420px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                          <h4 style={{ margin: '0 0 12px' }}>Permanently remove tenant?</h4>
                          <p style={{ margin: '0 0 16px', color: '#333' }}>
                            This will remove all traces of <strong>{purgeConfirmTenant.name}</strong> ({purgeConfirmTenant.subdomain}) from the system. This action cannot be undone and there is no option to restore.
                          </p>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn-secondary" onClick={() => setPurgeConfirmTenant(null)} disabled={purgeTenantLoading}>Cancel</button>
                            <button type="button" className="btn btn-danger" onClick={handleConfirmPurgeTenant} disabled={purgeTenantLoading}>
                              {purgeTenantLoading ? 'Removing...' : 'Confirm permanent removal'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {restoreTenantTarget && (
                      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => !restoreLoading2 && setRestoreTenantTarget(null)}>
                        <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', maxWidth: '520px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                          <h4 style={{ margin: '0 0 12px' }}>Restore Tenant: {restoreTenantTarget.name} ({restoreTenantTarget.subdomain})</h4>
                          <p style={{ margin: '0 0 16px', color: '#333', fontSize: '14px' }}>Select a backup ZIP file to restore:</p>
                          <input type="file" accept=".zip,.json" onChange={(e) => { if (e.target.files[0]) handleRestoreFileChosen(e.target.files[0]); }} style={{ marginBottom: '12px', display: 'block' }} disabled={restoreLoading2} />
                          {restoreLoading2 && !restoreDryRunResult && <p style={{ color: '#666' }}>Validating backup file...</p>}
                          {restoreDryRunResult && (
                            <div style={{ marginBottom: '16px', padding: '12px', background: '#f8f9fa', borderRadius: '6px', fontSize: '14px' }}>
                              <p style={{ margin: '0 0 6px' }}><strong>Backup from:</strong> {restoreDryRunResult.backupTenantName || 'Unknown'} ({restoreDryRunResult.backupTenantSubdomain || '?'})</p>
                              <p style={{ margin: '0 0 6px' }}><strong>Backup date:</strong> {restoreDryRunResult.backupDate ? new Date(restoreDryRunResult.backupDate).toLocaleString() : 'Unknown'}</p>
                              {restoreDryRunResult.tenantMismatch && (
                                <p style={{ margin: '8px 0', padding: '8px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', color: '#856404', fontWeight: 'bold' }}>Warning: This backup is from a different tenant ({restoreDryRunResult.backupTenantSubdomain}). Data will be restored to {restoreTenantTarget.subdomain}.</p>
                              )}
                              {restoreDryRunResult.hasExistingData && (
                                <p style={{ margin: '8px 0', padding: '8px', background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px', color: '#721c24', fontWeight: 'bold' }}>Warning: This tenant has existing data. All current data will be permanently wiped and replaced with the backup.</p>
                              )}
                            </div>
                          )}
                          {restoreMessage && <p style={{ margin: '0 0 12px', color: restoreMessage.includes('success') || restoreMessage.includes('completed') ? '#155724' : '#721c24', fontWeight: 'bold' }}>{restoreMessage}</p>}
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn-secondary" onClick={() => setRestoreTenantTarget(null)} disabled={restoreLoading2}>Cancel</button>
                            {restoreDryRunResult && (
                              <button type="button" className="btn btn-danger" onClick={handleRestoreConfirm} disabled={restoreLoading2}>
                                {restoreLoading2 ? 'Restoring...' : 'Confirm Restore'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {seedTenantId && (
                      <div style={{ marginTop: '16px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <h4 style={{ margin: '0 0 12px' }}>Seed first tenant admin</h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                          <input placeholder="Email" value={seedEmail} onChange={e => setSeedEmail(e.target.value)} style={{ padding: '8px 12px', width: '200px' }} />
                          <input type="password" placeholder="Password" value={seedPassword} onChange={e => setSeedPassword(e.target.value)} style={{ padding: '8px 12px', width: '160px' }} />
                          <input placeholder="Name" value={seedName} onChange={e => setSeedName(e.target.value)} style={{ padding: '8px 12px', width: '160px' }} />
                          <button type="button" className="btn btn-primary" onClick={handleSeedAdmin} disabled={seedLoading}>
                            {seedLoading ? 'Creating...' : 'Create Tenant Admin'}
                          </button>
                          <button type="button" className="btn btn-secondary" onClick={() => { setSeedTenantId(null); setSeedEmail(''); setSeedPassword(''); setSeedName(''); setSeedMessage(''); }}>Cancel</button>
                        </div>
                        {seedMessage && <p style={{ margin: '8px 0 0', color: seedMessage.startsWith('(') ? '#721c24' : '#155724' }}>{seedMessage}</p>}
                      </div>
                    )}

                    {lastSeedInfo && lastSeedInfo.subdomain && (
                      <div style={{ marginTop: '16px', padding: '16px', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '8px' }}>
                        <p style={{ margin: '0 0 6px', fontWeight: 600 }}>Tenant admin created</p>
                        <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Send this to the customer:</p>
                        <p style={{ margin: '0 0 4px' }}>Login URL: <a href={makeTenantUrl(lastSeedInfo.subdomain)} target="_blank" rel="noopener noreferrer">{makeTenantUrl(lastSeedInfo.subdomain)}</a></p>
                        <p style={{ margin: '0 0 4px' }}>Email: {lastSeedInfo.email}</p>
                        <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#495057' }}>Password: (temporary password you created)</p>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={async () => { const text = `Login: ${makeTenantUrl(lastSeedInfo.subdomain)}\nEmail: ${lastSeedInfo.email}`; const ok = await copyToClipboard(text); setSeedMessage(ok ? 'Login info copied' : 'Copy failed'); setTimeout(() => setSeedMessage(''), 2000); }}>Copy login info</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default PowerUserDashboard;


